import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getArticles, getFeaturedArticles } from '../services/firestoreService';
import { useAuth } from '../contexts/AuthContext';

const News = () => {
  const { hasRole } = useAuth();

  // State for articles data
  const [newsArticles, setNewsArticles] = useState([]);
  const [featuredArticles, setFeaturedArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [useFirebase, setUseFirebase] = useState(true);

  // State for filtering and sorting
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOrder, setSortOrder] = useState('newest'); // 'newest', 'oldest', 'comments', 'views'

  // Mock data fallback
  const mockNewsArticles = [
    {
      id: 2,
      title: 'APN Q1 NEWSLETTER',
      image: '/images/news/q1-newsletter.png',
      date: 'April 15, 2022',
      author: 'Caroline Burnham',
      category: 'News',
      excerpt: 'Check out our Q1 newsletter with updates on recent events and upcoming opportunities.',
      content: 'Full article content would go here...'
    }
  ];

  // Load articles from Firebase or fallback to static data
  useEffect(() => {
    loadArticles();
  }, [useFirebase, categoryFilter]);

  const loadArticles = async () => {
    try {
      setLoading(true);
      setError(null);

      if (useFirebase) {
        console.log('Loading articles from Firebase...');

        // Load articles based on category filter
        const articlesData = await getArticles(20, categoryFilter);
        console.log('Articles from Firebase:', articlesData);

        // Load featured articles
        const featuredData = await getFeaturedArticles(3);
        console.log('Featured articles from Firebase:', featuredData);

        setNewsArticles(articlesData);
        setFeaturedArticles(featuredData);

        // If no articles found in Firebase, fallback to static data
        if (articlesData.length === 0) {
          console.log('No articles found in Firebase, falling back to static data...');
          setUseFirebase(false);
          return;
        }
      } else {
        console.log('Loading articles from static data...');
        // Fallback to static data
        setNewsArticles(mockNewsArticles);
        setFeaturedArticles(mockNewsArticles.filter(article => article.featured));
      }
    } catch (err) {
      console.error('Error loading articles:', err);
      setError(err.message);

      // Fallback to static data on error
      if (useFirebase) {
        console.log('Firebase error, falling back to static data...');
        setUseFirebase(false);
      }
    } finally {
      setLoading(false);
    }
  };

  // Filter articles based on category and search term
  const filteredArticles = newsArticles.filter(article => {
    const matchesCategory = categoryFilter === 'all' || article.category.toLowerCase() === categoryFilter.toLowerCase();
    const matchesSearch = article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          article.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  // Sort articles based on sort order
  const sortedArticles = [...filteredArticles].sort((a, b) => {
    if (sortOrder === 'newest') {
      const dateA = a.publishedAt?.toDate ? a.publishedAt.toDate() : new Date(a.date);
      const dateB = b.publishedAt?.toDate ? b.publishedAt.toDate() : new Date(b.date);
      return dateB - dateA;
    } else if (sortOrder === 'oldest') {
      const dateA = a.publishedAt?.toDate ? a.publishedAt.toDate() : new Date(a.date);
      const dateB = b.publishedAt?.toDate ? b.publishedAt.toDate() : new Date(b.date);
      return dateA - dateB;
    }
    // For demo purposes, we'll just use the ID for 'comments' and 'views' sorting
    return a.id - b.id;
  });

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-apn-blue"></div>
      </div>
    );
  }

  return (
    <div className="bg-white">
      {/* Page Header */}
      <div className="bg-apn-blue text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            {/* APN Logo */}
            <div className="flex justify-center mb-4">
              <img
                src={`${process.env.PUBLIC_URL}/apn-logos/APN-Tag-white.jpg`}
                alt="APN Logo"
                className="h-16"
              />
            </div>
            <h1 className="text-3xl font-bold">News</h1>
            <p className="mt-2 text-xl">Stay Updated with APN</p>


          </div>
        </div>
      </div>

      {/* News Content */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Filters and Search */}
          <div className="mb-8 flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setCategoryFilter('all')}
                className={`px-4 py-2 rounded-md ${
                  categoryFilter === 'all'
                    ? 'bg-apn-blue text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                All
              </button>
              <button
                onClick={() => setCategoryFilter('feature')}
                className={`px-4 py-2 rounded-md ${
                  categoryFilter === 'feature'
                    ? 'bg-apn-blue text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Features
              </button>
              <button
                onClick={() => setCategoryFilter('news')}
                className={`px-4 py-2 rounded-md ${
                  categoryFilter === 'news'
                    ? 'bg-apn-blue text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                News
              </button>
            </div>
            <div className="w-full md:w-64">
              <input
                type="text"
                placeholder="Search news..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
              />
            </div>
          </div>

          {/* Sort Options */}
          <div className="mb-8 flex justify-center">
            <div className="inline-flex rounded-md shadow-sm" role="group">
              <button
                onClick={() => setSortOrder('newest')}
                className={`px-4 py-2 text-sm font-medium rounded-l-lg ${
                  sortOrder === 'newest'
                    ? 'bg-apn-blue text-white'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-100'
                }`}
              >
                Newest
              </button>
              <button
                onClick={() => setSortOrder('oldest')}
                className={`px-4 py-2 text-sm font-medium ${
                  sortOrder === 'oldest'
                    ? 'bg-apn-blue text-white'
                    : 'bg-white text-gray-700 border-t border-b border-gray-300 hover:bg-gray-100'
                }`}
              >
                Oldest
              </button>
              <button
                onClick={() => setSortOrder('comments')}
                className={`px-4 py-2 text-sm font-medium ${
                  sortOrder === 'comments'
                    ? 'bg-apn-blue text-white'
                    : 'bg-white text-gray-700 border-t border-b border-gray-300 hover:bg-gray-100'
                }`}
              >
                Most Comments
              </button>
              <button
                onClick={() => setSortOrder('views')}
                className={`px-4 py-2 text-sm font-medium rounded-r-lg ${
                  sortOrder === 'views'
                    ? 'bg-apn-blue text-white'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-100'
                }`}
              >
                Most Views
              </button>
            </div>
          </div>

          {/* News Articles */}
          {sortedArticles.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {sortedArticles.map(article => (
                <div key={article.id} className="bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200">
                  <Link to={`/news/${article.id}`} className="block h-48 bg-white relative overflow-hidden">
                    <div className="h-full w-full overflow-hidden flex items-center justify-center bg-white">
                      <img
                        src={`${process.env.PUBLIC_URL}${article.image}`}
                        alt={article.title}
                        className="h-full w-full object-contain transition-transform duration-300 hover:scale-105"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = `${process.env.PUBLIC_URL}/apn-logos/APN-blue (1).png`;
                        }}
                      />
                    </div>
                  </Link>
                  <div className="p-6">
                    <div className="flex items-center mb-2">
                      <span className="inline-block bg-apn-blue text-white text-xs px-2 py-1 rounded">
                        {article.category}
                      </span>
                      <span className="text-gray-500 text-sm ml-2">
                        {article.publishedAt?.toDate ?
                          article.publishedAt.toDate().toLocaleDateString() :
                          article.date
                        }
                      </span>
                    </div>
                    <h3 className="text-xl font-bold mb-2">{article.title}</h3>
                    <p className="text-gray-600 mb-4">{article.excerpt}</p>
                    <div className="flex items-center text-gray-500 text-sm mb-4">
                      <span>By {article.author}</span>
                    </div>
                    <Link
                      to={`/news/${article.id}`}
                      className="block w-full bg-apn-blue text-white text-center py-2 px-4 rounded hover:bg-blue-700 transition duration-200"
                    >
                      Read More
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-xl text-gray-600">No news articles found matching your search.</p>
            </div>
          )}

          {/* Pagination */}
          <div className="mt-12 flex justify-center">
            <nav className="inline-flex rounded-md shadow">
              <a href="#" className="px-4 py-2 rounded-l-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50">
                Previous
              </a>
              <a href="#" className="px-4 py-2 border-t border-b border-gray-300 bg-apn-blue text-white">
                1
              </a>
              <a href="#" className="px-4 py-2 border-t border-b border-gray-300 bg-white text-gray-700 hover:bg-gray-50">
                2
              </a>
              <a href="#" className="px-4 py-2 border-t border-b border-gray-300 bg-white text-gray-700 hover:bg-gray-50">
                3
              </a>
              <a href="#" className="px-4 py-2 rounded-r-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50">
                Next
              </a>
            </nav>
          </div>
        </div>
      </section>

      {/* Featured News */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-gray-900">Featured News</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {newsArticles
              .filter(article => article.category === 'Feature')
              .slice(0, 3)
              .map(article => (
                <div key={article.id} className="bg-white rounded-lg shadow-md overflow-hidden">
                  <Link to={`/news/${article.id}`} className="block h-40 bg-white relative overflow-hidden">
                    <div className="h-full w-full overflow-hidden flex items-center justify-center bg-white">
                      <img
                        src={`${process.env.PUBLIC_URL}${article.image}`}
                        alt={article.title}
                        className="h-full w-full object-contain transition-transform duration-300 hover:scale-105"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = `${process.env.PUBLIC_URL}/apn-logos/APN-blue (1).png`;
                        }}
                      />
                    </div>
                  </Link>
                  <div className="p-4">
                    <h3 className="font-bold text-lg mb-2">{article.title}</h3>
                    <p className="text-gray-600 text-sm mb-4">{article.excerpt}</p>
                    <Link to={`/news/${article.id}`} className="text-apn-blue hover:underline text-sm">
                      Read More →
                    </Link>
                  </div>
                </div>
              ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="bg-apn-blue text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          {/* APN Logo */}
          <div className="flex justify-center mb-6">
            <img
              src={`${process.env.PUBLIC_URL}/apn-logos/APN-Tag-SQ-white.jpg`}
              alt="APN Logo Square"
              className="h-20"
            />
          </div>
          <h2 className="text-3xl font-bold mb-4">Ready to Join the Network?</h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto">
            Connect with finance professionals, access exclusive resources, and accelerate your career with APN.
          </p>
          <Link to="/register" className="btn-primary bg-white text-apn-blue hover:bg-gray-100">
            Become a Member Today
          </Link>
        </div>
      </section>
    </div>
  );
};

export default News;
